import React, { useState, useEffect } from 'react';
import { 
  DocumentTextIcon, 
  CalendarIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { reportsAPI } from '../../api';

const Reports = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    reportType: 'daily',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    department: 'all'
  });

  useEffect(() => {
    loadReports();
  }, [filters]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.getDashboardReport(filters);
      setReportData(response.data);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = async (format = 'pdf') => {
    try {
      const response = await reportsAPI.exportReport({
        ...filters,
        format
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'text/csv' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hospital-report-${filters.reportType}-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Error exporting report. Please try again.');
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
      <div className="flex items-center">
        <div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        <div className="ml-4">
          <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  const ReportSection = ({ title, children }) => (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600">Hospital performance and operational reports</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => handleExportReport('pdf')}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            Export PDF
          </button>
          <button
            onClick={() => handleExportReport('csv')}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <FunnelIcon className="w-5 h-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
            <select
              value={filters.reportType}
              onChange={(e) => setFilters({...filters, reportType: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="daily">Daily Report</option>
              <option value="weekly">Weekly Report</option>
              <option value="monthly">Monthly Report</option>
              <option value="yearly">Yearly Report</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
            <select
              value={filters.department}
              onChange={(e) => setFilters({...filters, department: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Departments</option>
              <option value="opd">OPD</option>
              <option value="emergency">Emergency</option>
              <option value="inpatient">Inpatient</option>
              <option value="laboratory">Laboratory</option>
              <option value="radiology">Radiology</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading reports...</p>
        </div>
      ) : reportData ? (
        <>
          {/* Summary Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Patients"
              value={reportData.totalPatients || 0}
              subtitle="Registered patients"
              icon={DocumentTextIcon}
              color="blue"
            />
            <StatCard
              title="OPD Visits"
              value={reportData.opdVisits || 0}
              subtitle="Outpatient visits"
              icon={EyeIcon}
              color="green"
            />
            <StatCard
              title="Admissions"
              value={reportData.admissions || 0}
              subtitle="New admissions"
              icon={ChartBarIcon}
              color="purple"
            />
            <StatCard
              title="Revenue"
              value={`₹${reportData.revenue || 0}`}
              subtitle="Total collection"
              icon={CalendarIcon}
              color="yellow"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Patient Demographics */}
            <ReportSection title="Patient Demographics">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Male Patients</span>
                  <span className="font-semibold">{reportData.demographics?.male || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Female Patients</span>
                  <span className="font-semibold">{reportData.demographics?.female || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Age 0-18</span>
                  <span className="font-semibold">{reportData.demographics?.children || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Age 19-60</span>
                  <span className="font-semibold">{reportData.demographics?.adults || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Age 60+</span>
                  <span className="font-semibold">{reportData.demographics?.seniors || 0}</span>
                </div>
              </div>
            </ReportSection>

            {/* Department Utilization */}
            <ReportSection title="Department Utilization">
              <div className="space-y-4">
                {reportData.departments?.map((dept, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 capitalize">{dept.name}</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{width: `${dept.utilization}%`}}
                        ></div>
                      </div>
                      <span className="font-semibold text-sm">{dept.utilization}%</span>
                    </div>
                  </div>
                )) || (
                  <p className="text-gray-500 text-sm">No department data available</p>
                )}
              </div>
            </ReportSection>

            {/* Top Diagnoses */}
            <ReportSection title="Top Diagnoses">
              <div className="space-y-3">
                {reportData.topDiagnoses?.map((diagnosis, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{diagnosis.name}</span>
                    <span className="font-semibold">{diagnosis.count}</span>
                  </div>
                )) || (
                  <p className="text-gray-500 text-sm">No diagnosis data available</p>
                )}
              </div>
            </ReportSection>

            {/* Bed Occupancy */}
            <ReportSection title="Bed Occupancy">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Beds</span>
                  <span className="font-semibold">{reportData.bedOccupancy?.total || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Occupied</span>
                  <span className="font-semibold text-red-600">{reportData.bedOccupancy?.occupied || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Available</span>
                  <span className="font-semibold text-green-600">{reportData.bedOccupancy?.available || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Occupancy Rate</span>
                  <span className="font-semibold">{reportData.bedOccupancy?.rate || 0}%</span>
                </div>
              </div>
            </ReportSection>
          </div>

          {/* Recent Activity */}
          <ReportSection title="Recent Activity">
            <div className="space-y-3">
              {reportData.recentActivity?.map((activity, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                    <p className="text-xs text-gray-500">{activity.timestamp}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    activity.type === 'admission' ? 'bg-blue-100 text-blue-800' :
                    activity.type === 'discharge' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {activity.type}
                  </span>
                </div>
              )) || (
                <p className="text-gray-500 text-sm">No recent activity</p>
              )}
            </div>
          </ReportSection>
        </>
      ) : (
        <div className="text-center py-12">
          <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Report Data</h3>
          <p className="text-gray-500">Please select filters and try again.</p>
        </div>
      )}
    </div>
  );
};

export default Reports;